import type { ReactNode } from 'react';

/**
 * The small slice of Markdown a Diagnose report uses — paragraphs, bullet and
 * numbered lists, headings, **bold**, `code` — built as React elements. The
 * portal has no Markdown renderer, and the text is model output, so nothing
 * here ever reaches innerHTML: HTML in the source shows as literal text, and a
 * link shows as its text followed by the address rather than as an anchor.
 */

type Block =
  | { kind: 'p'; text: string }
  | { kind: 'h'; text: string }
  | { kind: 'ul' | 'ol'; items: string[] };

const BULLET = /^\s*[-*+]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;
const HEADING = /^\s*#{1,6}\s+(.*)$/;

function parseBlocks(source: string): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  let list: { kind: 'ul' | 'ol'; items: string[] } | null = null;

  const flush = () => {
    if (paragraph.length) blocks.push({ kind: 'p', text: paragraph.join(' ') });
    paragraph = [];
    if (list) blocks.push(list);
    list = null;
  };

  for (const raw of source.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flush();
      continue;
    }
    const heading = HEADING.exec(line);
    const bullet = BULLET.exec(line);
    const numbered = NUMBERED.exec(line);
    if (heading) {
      flush();
      blocks.push({ kind: 'h', text: heading[1] });
    } else if (bullet || numbered) {
      const kind = bullet ? 'ul' : 'ol';
      const text = (bullet ?? numbered)![1];
      if (paragraph.length || (list && list.kind !== kind)) flush();
      if (!list) list = { kind, items: [] };
      list.items.push(text);
    } else if (list) {
      // A continuation line belongs to the last list item.
      list.items[list.items.length - 1] += ` ${line.trim()}`;
    } else {
      paragraph.push(line.trim());
    }
  }
  flush();
  return blocks;
}

const INLINE = /(`[^`]+`|\*\*[^*]+\*\*|\[[^\]]+\]\([^)\s]+\))/g;

function renderInline(text: string): ReactNode[] {
  return text.split(INLINE).map((part, i) => {
    if (!part) return null;
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return (
        <code key={i} className="rounded bg-slate-900/70 px-1 py-0.5 font-mono text-[14px] text-slate-200 break-all">
          {part.slice(1, -1)}
        </code>
      );
    }
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={i} className="font-semibold text-slate-100">{part.slice(2, -2)}</strong>;
    }
    const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(part);
    if (link) return <span key={i}>{`${link[1]} (${link[2]})`}</span>;
    return <span key={i}>{part}</span>;
  });
}

export function SafeMarkdown({ source, className }: { source: string; className?: string }) {
  const blocks = parseBlocks(source);
  return (
    <div className={`space-y-3 text-[15px] leading-relaxed text-slate-300 break-words ${className ?? ''}`}>
      {blocks.map((block, i) => {
        if (block.kind === 'h') {
          return <h4 key={i} className="text-[15px] font-semibold text-slate-100">{renderInline(block.text)}</h4>;
        }
        if (block.kind === 'p') return <p key={i}>{renderInline(block.text)}</p>;
        const List = block.kind;
        return (
          <List key={i} className={`space-y-1 pl-5 ${List === 'ul' ? 'list-disc' : 'list-decimal'}`}>
            {block.items.map((item, j) => <li key={j}>{renderInline(item)}</li>)}
          </List>
        );
      })}
    </div>
  );
}

export default SafeMarkdown;
